// packages
import dayjs from 'dayjs';

// components
import FormInputSection from './components/FormInputSection/FormInputSection';
import FormActionBar from './components/FormActionBar/FormActionBar';
import ConfirmationDialog from 'components/shared/ConfirmationDialog';
import FormHeader from './components/FormHeader/FormHeader';

// form
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import schema from './AppointmentForm.schema';

// hooks
import { useContext, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useEntityListener } from 'components/mrp/mrp.hooks';

// types
import {
  type AppointmentFormFieldData,
  AppointmentRecurrenceType
} from './AppointmentForm.types';
import { DialogType } from 'components/shared/DialogProvider/DialogProvider.enums';
import type { CustomSelectRef } from 'components/shared/Select';

// stores
import { useAppointmentStore } from 'components/mrp/stores/appointmentStore/appointmentStore';
import { useCalendarStore } from 'components/mrp/stores/calendarStore/calendarStore';
import useSelectedDateRange from 'components/roster-portal/activity-planning/hooks/useSelectedDateRange';
import { useShallow } from 'zustand/shallow';

// utils
import { appointmentFormFieldEntitiesToPlanningEntityIdentifiers } from './AppointmentForm.utils';
import { DialogContext } from 'components/shared/DialogProvider/DialogContext';
import { isPrimaryModifier } from 'components/shared/Typography/Kbd/Kbd.utils';

// styles
import { AppointmentFormContainer } from './AppointmentForm.styles';

// components
import { Container } from './AppointmentFormContainer.styles';

const AppointmentForm = () => {
  'use memo';
  // Even with the React compiler and 'use memo' annotation, the useMemo's in this component are still
  // necessary — without it, the derived array is recreated on every render, causing
  // downstream hooks to see a new reference each time and triggering a maximum update depth error.

  const { t } = useTranslation('common');
  const formContainerRef = useRef<HTMLDivElement>(null);
  const activityFieldRef = useRef<HTMLInputElement>(null);
  const recurrenceFieldRef = useRef<CustomSelectRef>(null);

  const calendarIdsPerEntityId = useCalendarStore(
    useShallow((state) => state.calendarIdsPerEntityId)
  );
  const closeCalendar = useCalendarStore(
    useShallow((state) => state.control.closeCalendar)
  );

  const {
    appointment,
    original,
    showCloseWarning,
    dirty,
    setDirty,
    closeAppointment,
    cancelCloseAppointment,
    setPreviewData
  } = useAppointmentStore(
    useShallow((state) => ({
      appointment: state.appointment,
      original: state.original,
      showCloseWarning: state.showCloseWarning,
      dirty: state.dirty,
      setDirty: state.control.setDirty,
      closeAppointment: state.control.closeAppointment,
      cancelCloseAppointment: state.control.cancelCloseAppointment,
      setPreviewData: state.control.setPreviewData
    }))
  );

  const { startDate } = useSelectedDateRange();

  // useMemo to avoid recalculating default values on every render, only when appointment or startDate changes
  // the react compiler did not work without it
  // TODO: investigate what we are doing wrong
  const defaultValues: AppointmentFormFieldData = useMemo(() => {
    const weekStart = dayjs(startDate).startOf('day');
    const tomorrow = dayjs().add(1, 'day').startOf('day');
    const defaultDate = weekStart > tomorrow ? weekStart : tomorrow;

    return schema.cast({
      start: defaultDate.add(9, 'hours').toISOString(),
      end: defaultDate.add(9, 'hours').add(30, 'minutes').toISOString(),
      recurrencePattern: {
        daysSelected: [defaultDate.isoWeekday() - 1]
      },
      editAppointmentType: null,
      notePublic: '',
      noteInternal: '',
      isRecurring: false,
      activity: null,
      planEntities: {
        EMPLOYEE: [],
        CLIENT: [],
        WORKPLACE: [],
        RESOURCE: []
      },
      ...appointment
    });
  }, [appointment, startDate]);

  const methods = useForm<AppointmentFormFieldData>({
    defaultValues,
    resolver: yupResolver(schema)
  });

  const {
    handleSubmit,
    getValues,
    formState: { dirtyFields }
  } = methods;
  const [start, end, recurrencePattern] = useWatch({
    control: methods.control,
    name: ['start', 'end', 'recurrencePattern']
  });

  const [clients, employees, resources, workplace] = useWatch({
    control: methods.control,
    name: [
      'planEntities.CLIENT',
      'planEntities.EMPLOYEE',
      'planEntities.RESOURCE',
      'planEntities.WORKPLACE'
    ]
  });

  const selectedPlanningEntityIdentifiers = useMemo(
    () =>
      appointmentFormFieldEntitiesToPlanningEntityIdentifiers([
        ...clients,
        ...employees,
        ...resources,
        ...workplace
      ]),
    [clients, employees, resources, workplace]
  );

  const refetchIdentifiers = useMemo(
    () =>
      [
        ...selectedPlanningEntityIdentifiers,
        ...(!!original ? original.planningEntityIdentifiers : [])
      ]
        // Only refetch entities of which the calendar is opened
        .filter(
          ({ entityId }) =>
            entityId != null && calendarIdsPerEntityId[entityId] !== undefined
        ),
    [selectedPlanningEntityIdentifiers, original, calendarIdsPerEntityId]
  );

  const selectedEntityIds = useMemo(
    () => selectedPlanningEntityIdentifiers.map((entity) => entity.entityId!),
    [selectedPlanningEntityIdentifiers]
  );

  const { refetchByPlanningEntityIdentifiers } = useEntityListener({
    listen: false,
    planningEntityIdentifiers: refetchIdentifiers
  });

  const { openDialog, onClose: onCloseDialog } = useContext(DialogContext);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const saveAppointment = (data: AppointmentFormFieldData) => {
    const current = appointment?.recurrencePattern;
    const next = data.recurrencePattern;

    const recurrencePatternChanged =
      appointment?.isRecurring &&
      (next?.recurrenceRule !== current?.recurrenceRule ||
        next?.recurrenceInterval !== current?.recurrenceInterval ||
        next?.recurrenceRangeType !== current?.recurrenceRangeType ||
        (next?.recurrenceRangeType === 'END_ON_DATE' &&
          next?.recurrenceEnd !== current?.recurrenceEnd));

    const updatedData = {
      ...data,
      originalOccurrenceStart: appointment?.start,
      ...(recurrencePatternChanged && {
        editAppointmentType: AppointmentRecurrenceType.THIS_AND_ALL_SUBSEQUENT
      })
    };

    openDialog?.(
      'smart-action-dialog-appointmentform-update',
      DialogType.SMART_ACTION_DIALOG,
      {
        actionType: appointment?.id ? 'update' : 'create',
        appointmentExceptions: appointment?.appointmentExceptions,
        payload: updatedData
      }
    );
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const closeAppointmentForm = (forceClose: boolean) => {
    const calendarIds = selectedEntityIds.flatMap(
      (id) => calendarIdsPerEntityId[id]
    );

    calendarIds.forEach((id) => {
      // only close unpinned calendars
      closeCalendar(id);
    });

    closeAppointment(forceClose);
  };

  // Helper function to check if event originated from within the form
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const isEventFromForm = (event: KeyboardEvent): boolean =>
    formContainerRef.current !== null &&
    event.target instanceof Node &&
    formContainerRef.current.contains(event.target);

  useEffect(() => {
    setPreviewData({
      id: getValues('id'),
      start: getValues('start')!,
      end: getValues('end')!,
      entityIds: selectedEntityIds,
      recurrencePattern: recurrencePattern
    });
  }, [
    start,
    end,
    clients,
    employees,
    resources,
    workplace,
    recurrencePattern,
    setPreviewData,
    getValues,
    selectedEntityIds
  ]);

  useEffect(() => {
    const hasDirtyFields = Object.keys(dirtyFields).length > 0;
    if (dirty !== hasDirtyFields) setDirty(hasDirtyFields);
  }, [dirty, dirtyFields, setDirty]);

  useEffect(() => {
    if (appointment) {
      methods.reset(defaultValues);

      // Focus recurrence field if new appointment, otherwise activity field
      requestAnimationFrame(() => {
        if (appointment.id) {
          activityFieldRef.current?.focus();
        } else {
          recurrenceFieldRef.current?.focus();
        }
      });
    }
  }, [appointment, defaultValues, methods]);

  useEffect(() => {
    onCloseDialog?.(
      'smart-action-dialog-appointmentform-update',
      DialogType.SMART_ACTION_DIALOG,
      (props) => {
        if (props?.ok) {
          refetchByPlanningEntityIdentifiers([...refetchIdentifiers]);
          closeAppointmentForm(true);
        }
      }
    );
  }, [
    onCloseDialog,
    appointment,
    refetchByPlanningEntityIdentifiers,
    refetchIdentifiers,
    closeAppointmentForm
  ]);

  // Close form on esc (only when focus is within the form and no dropdowns are open)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        // Only handle if event originated from Appointment Form
        if (isEventFromForm(event)) {
          closeAppointmentForm(false);
        }
      }
    };

    // Attach listener to document but check containment
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [closeAppointmentForm, isEventFromForm]);

  // Submit form on PRIMARY + S (only when focus is within the form)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() === 's' && isPrimaryModifier(event)) {
        // Only handle if event originated from Appointment Form
        if (isEventFromForm(event)) {
          // Prevent browser's default save dialog
          event.preventDefault();
          event.stopPropagation();

          handleSubmit((data) => saveAppointment(data))();
        }
      }
    };

    // Attach listener to document but check containment
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleSubmit, isEventFromForm, saveAppointment]);

  return (
    <Container>
      <FormProvider {...methods}>
        {/* prevents default form submission on ENTER but still uses form semantics */}
        <form onSubmit={(e) => e.preventDefault()} style={{ height: '100%' }}>
          <AppointmentFormContainer ref={formContainerRef}>
            <FormHeader />

            <FormInputSection
              activityFieldRef={activityFieldRef}
              recurrenceFieldRef={recurrenceFieldRef}
            />

            <FormActionBar
              onSave={handleSubmit((data) => saveAppointment(data))}
              onCancel={() => closeAppointmentForm(false)}
            />
          </AppointmentFormContainer>
        </form>
      </FormProvider>

      {showCloseWarning && (
        <ConfirmationDialog
          handleAction={(isConfirmed) => {
            if (isConfirmed) {
              closeAppointmentForm(true);
            } else {
              cancelCloseAppointment();
            }
          }}
          cancelBtnText={t('no')}
          confirmBtnText={t('yes')}
          type="warning"
          title={t('mrp.appointmentForm.confirmCloseTitle')}
        >
          <p>{t('mrp.appointmentForm.confirmCloseMessage')}</p>
        </ConfirmationDialog>
      )}
    </Container>
  );
};

export default AppointmentForm;
